"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { cn } from "@/lib/utils";
import type { ImageDataType } from "@/shared/types";
import type { GalleryControlsTheme } from "./types";
import {
  buildCarouselSlides,
  getCarouselInitialSlide,
  normalizeCarouselIndex,
  shouldResetCarouselPosition,
} from "./carouselSlides";
import { GalleryNavButton } from "./GalleryNavButton";
import { GalleryPagination } from "./GalleryPagination";
import "swiper/css";

interface ImageCarouselProps {
  images: ImageDataType[];
  onImageClick: (index: number) => void;
  controlsTheme?: GalleryControlsTheme;
  isAutoplayPaused?: boolean;
  className?: string;
}

const CAROUSEL_AUTOPLAY_DELAY_MS = 5000;

/** Figma block_carousel — peek slides, arrows on md+, pagination on mobile. */
const CAROUSEL_IMAGE_CLASS =
  "relative block h-full w-full cursor-pointer overflow-hidden aspect-[343/350] md:aspect-[720/450] desktop:aspect-[880/500]";

const CAROUSEL_BREAKPOINTS = {
  768: { slidesPerView: 1.2, spaceBetween: 8 },
  1440: { slidesPerView: 1.2, spaceBetween: 8 },
} as const;

export function ImageCarousel({
  images,
  onImageClick,
  controlsTheme,
  isAutoplayPaused = false,
  className,
}: ImageCarouselProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselSlides = useMemo(() => buildCarouselSlides(images), [images]);
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    const autoplay = swiperRef.current?.autoplay;
    if (!autoplay) {
      return;
    }

    if (isAutoplayPaused) {
      autoplay.stop();
      return;
    }

    autoplay.start();
  }, [isAutoplayPaused]);

  if (images.length === 0) {
    return null;
  }

  const goToSlide = (index: number) => {
    const swiper = swiperRef.current;
    if (!swiper || !hasMultipleImages) {
      return;
    }

    const currentSetStart =
      Math.floor(swiper.activeIndex / images.length) * images.length;
    swiper.slideTo(currentSetStart + index);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    const resetDirection = shouldResetCarouselPosition(
      swiper.activeIndex,
      images.length,
      carouselSlides.length
    );

    if (resetDirection === "backward") {
      const nextIndex = swiper.activeIndex + images.length;
      swiper.slideTo(nextIndex, 0, false);
      setActiveIndex(normalizeCarouselIndex(nextIndex, images.length));
      return;
    }

    if (resetDirection === "forward") {
      const nextIndex = swiper.activeIndex - images.length;
      swiper.slideTo(nextIndex, 0, false);
      setActiveIndex(normalizeCarouselIndex(nextIndex, images.length));
      return;
    }

    setActiveIndex(normalizeCarouselIndex(swiper.activeIndex, images.length));
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 pb-10 md:gap-4 md:pb-0",
        className
      )}
    >
      <div className="relative w-full max-w-full overflow-hidden">
        <Swiper
          modules={[Autoplay]}
          spaceBetween={8}
          slidesPerView={1.18}
          centeredSlides
          initialSlide={getCarouselInitialSlide(images)}
          watchSlidesProgress
          breakpoints={CAROUSEL_BREAKPOINTS}
          autoplay={
            hasMultipleImages
              ? {
                  delay: CAROUSEL_AUTOPLAY_DELAY_MS,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          className="w-full max-w-full overflow-hidden [&_.swiper-slide]:!h-auto [&_.swiper-wrapper]:items-stretch"
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            setActiveIndex(normalizeCarouselIndex(swiper.activeIndex, images.length));

            if (hasMultipleImages && !isAutoplayPaused && swiper.autoplay) {
              swiper.autoplay.start();
            }
          }}
          onSlideChange={handleSlideChange}
        >
          {carouselSlides.map((slide) => (
            <SwiperSlide key={slide.key} className="!h-auto">
              <button
                type="button"
                onClick={() => onImageClick(slide.realIndex)}
                className={CAROUSEL_IMAGE_CLASS}
                aria-label={`Открыть изображение ${slide.realIndex + 1}`}
              >
                <Image
                  src={slide.image.src}
                  alt={slide.image.alt || ""}
                  title={slide.image.title ?? undefined}
                  fill
                  sizes="(max-width: 767px) 100vw, (max-width: 1439px) 90vw, 1100px"
                  className="object-cover"
                  priority={slide.realIndex === 0}
                />
              </button>
            </SwiperSlide>
          ))}
        </Swiper>

        {hasMultipleImages && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-between md:flex">
            <GalleryNavButton
              direction="prev"
              ariaLabel="Предыдущее изображение"
              onClick={() => swiperRef.current?.slidePrev()}
              themeClassName={controlsTheme?.navButton}
              className="pointer-events-auto"
            />
            <GalleryNavButton
              direction="next"
              ariaLabel="Следующее изображение"
              onClick={() => swiperRef.current?.slideNext()}
              themeClassName={controlsTheme?.navButton}
              className="pointer-events-auto"
            />
          </div>
        )}
      </div>

      {hasMultipleImages && (
        <GalleryPagination
          total={images.length}
          activeIndex={activeIndex}
          onSelect={goToSlide}
          dotActiveClass={controlsTheme?.paginationDotActive}
          dotInactiveClass={controlsTheme?.paginationDotInactive}
          className="md:hidden"
        />
      )}
    </div>
  );
}
